import React, { useEffect, useState, useRef, useCallback, useMemo, useContext } from 'react';
import { secureGet, secureDelete, securePost } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";

import { PrimaryButton } from '../../components/PrimaryButton';
import GenericCard from './GenericCard';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import ModuleWrapper from '../../components/ModuleWrapper';
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from '../../Shared/UserContext';
import UserDetailsPopup from './UserDetailsPopup';
import TableLoadingArea from '../../components/TableLoadingArea';

import AddIcon from '../../icons/plusB.svg';
import EditIcon from '../../icons/edit.svg';
import DeleteIcon from '../../icons/trashR.svg';
import checkIcon from '../../icons/key.svg';

const Utilisateurs = () => {
  const { state: { role } } = useContext(UserContext);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPageUsers, setCurrentPageUsers] = useState([]);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState({
    needle: "",
    role: "",
    sortBy: "",
  });
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showModule, setShowModule] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showGoalModule, setShowGoalModule] = useState(false);
  const [userForGoal, setUserForGoal] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUserForDetails, setSelectedUserForDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  const roleKeys = Object.keys(t('roles', { returnObjects: true }) || {});
  const topRef = useRef(null);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "role",
      label: t(`filters.users.role`),
      type: "dropdown",
      options: roleKeys.map((roleKey) => ({
        value: roleKey,
        label: t(`roles.${roleKey}`),
      })),
    },
    {
      name: "sortBy",
      type: "dropdown",
      label: t(`filters.users.sortBy`),
      options: [
        { value: "name", label: "Nom" },
        { value: "role", label: "Rôle" },
      ],
    },
  ];

  const updatePagination = useCallback((data, page, size) => {
    setTotalPages(Math.ceil(data.length / size));
    setTotalElements(data.length);
    setCurrentPageUsers(paginateData(data, page, size));
  }, [setCurrentPageUsers]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet('/users', showSnackbar, { signal });

      console.log('Fetched users:', response.data);
      setUsers(response.data);
      setFilteredUsers(response.data);
      setCurrentPage(0); 
      updatePagination(response.data, 0, pageSize);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching users:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [signal, pageSize, updatePagination]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchUsers();
    return () => {
      abortController.abort();
    };
  }, [abortController, fetchUsers]);

  const applyFilters = useCallback(() => {
    let filtered = [...users];

    if (filters.needle) {
      const searchTerm = filters.needle.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          (user.firstName || "").toLowerCase().includes(searchTerm) ||
          (user.lastName || "").toLowerCase().includes(searchTerm) ||
          (user.email || "").toLowerCase().includes(searchTerm)
      );
    }

    if (filters.role) {
      filtered = filtered.filter((user) => user.role === filters.role);
    }

    if (filters.sortBy === "name") {
      filtered.sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
      );
    } else if (filters.sortBy === "role") {
      filtered.sort((a, b) => t(`roles.${a.role}`).localeCompare(t(`roles.${b.role}`)));
    }

    setFilteredUsers(filtered);
    updatePagination(filtered, 0, pageSize);
  }, [filters, users, pageSize, updatePagination, t]);

  const paginateData = (data, page, size) => {
    const startIndex = page * size;
    const endIndex = startIndex + size;
    return data.slice(startIndex, endIndex);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setCurrentPageUsers(paginateData(filteredUsers, page, pageSize));
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    updatePagination(filteredUsers, 0, newPageSize);
  };

  const handleFilterChange = (filterName, filterValue) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: filterValue,
    }));
  };

  const resetFilters = () => {
    setFilters({
      needle: "",
      role: "",
      sortBy: "",
    });
    setFilteredUsers(users);
    updatePagination(users, 0, pageSize);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);

    try {
      const deleteUrl = `/users/delete/${itemToDelete.id}`;
      await secureDelete(deleteUrl, showSnackbar);

      const updatedUsers = users.filter((user) => user.id !== itemToDelete.id);
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      updatePagination(updatedUsers, currentPage, pageSize);
      showSnackbar(t("confirmation.userDelete"), "success");
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setDeletePopupVisible(false);
      setDeleting(false);
    }
  };

  const handleGenerateSecretCode = async (user) => {
    try {
      const response = await securePost(`/users/${user.id}/secret-code/generate`, {}, showSnackbar);
      console.log("Secret code generated:", response.data);
      showSnackbar(t("confirmation.secretCodeGenerated"), "success");
    } catch (error) {
      console.error("Error generating secret code:", error);
    }
  };

  const getMenuOptions = (user) => {
    const options = [
      {
        label: t(`actions.modify`),
        icon: EditIcon,
        action: () => handleEditClick(user),
      },
    ];
    if (role === 'ROLE_WORKSHOP_MANAGER' && user.role === 'ROLE_TECHNICIAN' && user.secretCode === null) {
      options.push({
        label: t(`actions.generateSecretCode`),
        icon: checkIcon,
        action: () => handleGenerateSecretCode(user),
      });
    }
    if (role === 'ROLE_SERVICE_CONSULTANT') {
      options.push({
        label: t(`actions.delete`),
        icon: DeleteIcon,
        color: '#EF476F',
        action: () => handleDeleteClick(user),
      });
    }


    return options;
  };

  const handleModuleClose = (action = "cancel") => {
    if (action === "submit") {

      fetchUsers();
    }
    else if (action === "cancel") {
      console.log("Form closed without submission, no action taken.");
    }

    setShowModule(false);
    setSelectedUser(null);
  };

  const handleAddClick = () => {
    setSelectedUser(null);
    setShowModule(true);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setShowModule(true);
  };

  useEffect(() => {
    applyFilters();
  }, [filters, users, applyFilters]);

  const handleSetGoalClick = (user) => {
    setUserForGoal(user);
    setShowGoalModule(true);
  };

  const handleGoalModuleClose = (updatedGoal) => {
    if (updatedGoal) {
      console.log("Goal updated successfully", updatedGoal);
    } else {
      console.log("Goal form closed without changes.");
    }
    setShowGoalModule(false);
    setUserForGoal(null);
  };

  const handleCardClick = (user) => {
    setSelectedUserForDetails(user);
    setShowUserDetails(true);
  };

  const handleUserDetailsClose = () => {
    setShowUserDetails(false);
    setSelectedUserForDetails(null);
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - {t("titles.users")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.users`)}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t(`primaryButtons.add_user`)}
            icon={AddIcon}
            onClick={handleAddClick} />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <TableLoadingArea loading={loading} minHeight={200} className="mt-6">
        {/* User Cards */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {!loading && currentPageUsers.map((user, index) => (
            <div key={index} onClick={() => handleCardClick(user)} className="cursor-pointer">
              <GenericCard
                title={`${user.firstName || ''} ${user.lastName || ''}`.trim() || ''}
                subtitle={t(`roles.${user.role}`) || ''}
                optionsMenu={
                  <div onClick={(e) => e.stopPropagation()}>
                    <OptionsMenu options={getMenuOptions(user)} />
                  </div>
                }
                statusTag={
                  user.status ? (
                    <span
                      className={`px-2 py-1 rounded-full text-sm font-medium ${user.status === 'active' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                        }`}
                    >
                      {user.status}
                    </span>
                  ) : null
                }
              />
            </div>
          ))}
        </div>

        {!loading && currentPageUsers.length === 0 && (
          <div className="text-center mt-4">
            <p></p>
          </div>
        )}
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      {showModule && (
        <ModuleWrapper
          moduleType="users"
          mode={selectedUser ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedUser || {}}
        />
      )}

      {showGoalModule && (
        <ModuleWrapper
          moduleType="userobj"
          mode="modify"
          onClose={handleGoalModuleClose}
          initialValues={userForGoal || {}}
        />
      )}

      {showUserDetails && selectedUserForDetails && (
        <UserDetailsPopup
          user={selectedUserForDetails}
          onClose={handleUserDetailsClose}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />

    </div>
  );
}

export default Utilisateurs;